import { api } from "../common/utils"

/**
 * Client for bb-admin-agent — the internal staff AI agent service.
 *
 * Auth: short-lived HMAC staff token minted by the monolith
 * (GET /api/v1/staff-agent-token — session-authenticated, administrator only),
 * sent to the agent service as `x-staff-token`. Tokens live 1 hour; we re-mint
 * before expiry and retry once on 401.
 *
 * The agent service streams chat over POST-based SSE, so requests use fetch
 * (EventSource cannot set headers, and the shared axios instance points at
 * the monolith).
 */

const AGENT_URL =
  process.env.REACT_APP_ADMIN_AGENT_URL ??
  (process.env.NODE_ENV === "development" ? "http://localhost:3010" : "")

const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000

let cachedToken: { token: string; expiresAt: number } | null = null

async function mintStaffToken(): Promise<string> {
  const { data } = await api.get("/api/v1/staff-agent-token")
  if (data?.status !== "success" || !data?.token) {
    throw new Error(data?.message || "Failed to mint staff agent token")
  }
  cachedToken = {
    token: data.token,
    expiresAt: (data.expires_at ?? 0) * 1000
  }
  return data.token
}

async function getStaffToken(): Promise<string> {
  if (
    cachedToken &&
    cachedToken.expiresAt - Date.now() > TOKEN_REFRESH_MARGIN_MS
  ) {
    return cachedToken.token
  }
  return mintStaffToken()
}

async function agentFetch(
  path: string,
  init: RequestInit = {},
  retryOn401 = true
): Promise<Response> {
  if (!AGENT_URL) {
    throw new Error(
      "Staff agent service is not configured (REACT_APP_ADMIN_AGENT_URL)"
    )
  }
  const token = await getStaffToken()
  const res = await fetch(`${AGENT_URL}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      "x-staff-token": token,
      ...(init.headers ?? {})
    }
  })
  if (res.status === 401 && retryOn401) {
    cachedToken = null
    return agentFetch(path, init, false)
  }
  return res
}

/**
 * POST /api/chat/message/stream — returns the raw Response whose body is an
 * SSE stream (events: text, tool_call, tool_result, done, error). The caller
 * owns reading/parsing (see use-staff-chat-stream).
 */
export async function sendStaffChatMessageStream(
  message: string,
  conversationId: string | undefined,
  page: string | undefined,
  signal: AbortSignal
): Promise<Response> {
  const res = await agentFetch("/api/chat/message/stream", {
    method: "POST",
    body: JSON.stringify({ message, conversationId, page, client: "web" }),
    signal
  })
  if (!res.ok || !res.body) {
    let detail = ""
    try {
      detail = (await res.json())?.error ?? ""
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Agent request failed (${res.status})`)
  }
  return res
}

export interface StaffConversationSummary {
  id: string
  title: string
  messageCount: number
  pinned: boolean
  createdAt: string
  updatedAt: string
}

export interface StaffConversationDetail {
  id: string
  messages: {
    role: "user" | "assistant"
    content: string
    timestamp?: string
  }[]
  createdAt: string
  updatedAt: string
}

export async function listStaffConversations(): Promise<
  StaffConversationSummary[]
> {
  const res = await agentFetch("/api/conversations")
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error || `Failed to load history (${res.status})`)
  }
  return data.conversations ?? []
}

export async function getStaffConversation(
  id: string
): Promise<StaffConversationDetail> {
  const res = await agentFetch(`/api/conversations/${id}`)
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(
      data?.error || `Failed to load conversation (${res.status})`
    )
  }
  return data.conversation
}

export async function pinStaffConversation(
  id: string,
  pinned: boolean
): Promise<void> {
  const res = await agentFetch(
    `/api/conversations/${id}/${pinned ? "pin" : "unpin"}`,
    { method: "POST" }
  )
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(
      data?.error || `Failed to ${pinned ? "pin" : "unpin"} (${res.status})`
    )
  }
}

export async function deleteStaffConversation(id: string): Promise<void> {
  const res = await agentFetch(`/api/conversations/${id}`, {
    method: "DELETE"
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(
      data?.error || `Failed to delete conversation (${res.status})`
    )
  }
}

export interface PendingActionResult {
  status: string
  result?: unknown
  error?: string
}

export async function approvePendingAction(
  id: string
): Promise<PendingActionResult> {
  const res = await agentFetch(`/api/actions/${id}/approve`, { method: "POST" })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error || `Approve failed (${res.status})`)
  }
  return data
}

export async function rejectPendingAction(
  id: string
): Promise<PendingActionResult> {
  const res = await agentFetch(`/api/actions/${id}/reject`, { method: "POST" })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error || `Reject failed (${res.status})`)
  }
  return data
}
